import type { CellShape, NoteColors, NoteId } from '../types';
import { DEFAULT_NOTE_COLORS, WELCOME_MESSAGE } from '../constants';
import { NOTE_LABELS } from '../types';

const COLOR_NOTES: NoteId[] = ['do', 're', 'mi', 'fa', 'sol', 'la', 'si', 'rest'];

interface Props {
  open: boolean;
  onClose: () => void;
  colors: NoteColors;
  onColorsChange: (colors: NoteColors) => void;
  shape: CellShape;
  onShapeChange: (s: CellShape) => void;
  measureCount: number;
  onMeasureCountChange: (n: number) => void;
  beatsPerMeasure: number;
  onBeatsChange: (n: number) => void;
  onShowWelcome: () => void;
  rhythmMode?: boolean;
}

export function SettingsPanel({
  open,
  onClose,
  colors,
  onColorsChange,
  shape,
  onShapeChange,
  measureCount,
  onMeasureCountChange,
  beatsPerMeasure,
  onBeatsChange,
  onShowWelcome,
  rhythmMode = false,
}: Props) {
  if (!open) return null;

  const updateColor = (note: NoteId, value: string) => {
    onColorsChange({ ...colors, [note]: value });
  };

  return (
    <div className="settings-overlay" onClick={onClose} role="presentation">
      <div
        className="settings-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Configuración"
      >
        <header className="settings-header">
          <h2>Configuración</h2>
          <button type="button" className="btn-icon" onClick={onClose} aria-label="Cerrar">
            ✕
          </button>
        </header>

        {!rhythmMode && (
        <section className="settings-section">
          <h3>Colores de cada nota</h3>
          <p className="settings-hint">Estos colores se usan en todo el patrón.</p>
          <ul className="color-list">
            {COLOR_NOTES.map((note) => (
              <li key={note} className="color-row">
                <span className="color-row__label">{NOTE_LABELS[note]}</span>
                <input
                  type="color"
                  value={colors[note]}
                  onChange={(e) => updateColor(note, e.target.value)}
                  aria-label={`Color de ${NOTE_LABELS[note]}`}
                />
                <span
                  className="color-preview"
                  style={{ background: colors[note] }}
                />
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => onColorsChange({ ...DEFAULT_NOTE_COLORS })}
          >
            Restaurar colores boomwhackers
          </button>
        </section>
        )}

        {!rhythmMode && (
        <section className="settings-section">
          <h3>Forma de las notas</h3>
          <div className="shape-toggle" role="group">
            <button
              type="button"
              className={`btn ${shape === 'circle' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => onShapeChange('circle')}
              aria-pressed={shape === 'circle'}
            >
              Círculo
            </button>
            <button
              type="button"
              className={`btn ${shape === 'square' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => onShapeChange('square')}
              aria-pressed={shape === 'square'}
            >
              Cuadrado
            </button>
          </div>
        </section>
        )}

        <section className="settings-section">
          <h3>Compás</h3>
          <label className="settings-field">
            Tiempos por compás:
            <select
              value={beatsPerMeasure}
              onChange={(e) => onBeatsChange(Number(e.target.value))}
            >
              <option value={2}>2/4</option>
              <option value={3}>3/4</option>
              <option value={4}>4/4</option>
            </select>
          </label>
          <label className="settings-field">
            Cantidad de compases:
            <input
              type="number"
              min={1}
              max={8}
              value={measureCount}
              onChange={(e) =>
                onMeasureCountChange(Math.min(8, Math.max(1, Number(e.target.value))))
              }
            />
          </label>
        </section>

        {!rhythmMode && (
        <section className="settings-section">
          <button type="button" className="btn btn-secondary" onClick={onShowWelcome}>
            Ver mensaje de bienvenida
          </button>
          <p className="settings-hint settings-school">{WELCOME_MESSAGE}</p>
        </section>
        )}
      </div>
    </div>
  );
}
